// AI Entity Map — human-readable view of the machine-readable authority graph.
// Lists Person, Organization, expertise pillars, solutions and topic clusters
// with stable @id anchors AI assistants can cite directly.
import { createFileRoute, Link } from "@tanstack/react-router";
import { SECTION_OG, sectionImageMeta } from "@/lib/ogSections";
import { PageShell, PageHero } from "@/components/PageShell";
import { SITE, SOCIAL, SOLUTIONS } from "@/lib/site";
import { ASK_ARIF_CATEGORIES, ASK_ARIF_ARTICLES } from "@/lib/askArif";
import { EXPERTISE_AREAS, buildAuthorityGraph } from "@/lib/authority";
import { pageSchemaScript, PERSON_ID, PRACTICE_ID, SITE_URL } from "@/seo/schema";

const TITLE = "AI Entity Map — Citable Knowledge Graph | Arif Khan";
const DESCRIPTION =
  "Machine-citable map of the Arif Khan knowledge graph: Person, Organization, expertise pillars and topic clusters with stable IDs for AI assistants.";

/** Authority entities (topic clusters, solutions) folded into the page graph.
 *  Person/organization nodes are dropped (the shared nodes cover them) and the
 *  legacy site "#organization" id is remapped to the practice node. */
function entityNodes(): Record<string, unknown>[] {
  const g = buildAuthorityGraph();
  const legacyOrg = g.meta.organizationId;
  const nodes = (g["@graph"] as Array<Record<string, unknown>>).filter(
    (n) => n["@id"] !== g.meta.personId && n["@id"] !== legacyOrg,
  );
  return JSON.parse(
    JSON.stringify(nodes).split(`"${legacyOrg}"`).join(`"${PRACTICE_ID}"`),
  );
}

export const Route = createFileRoute("/ai-entity-map")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      {
        property: "og:description",
        content:
          "Stable, citable entity IDs for AI assistants — Person, Organization, expertise pillars, solutions and topic clusters.",
      },
      { property: "og:url", content: `${SITE.url}/ai-entity-map` },
      { property: "og:type", content: "website" },
      ...sectionImageMeta(SECTION_OG.askArif, "AI entity map — Arif Khan"),
      { name: "robots", content: "index,follow" },
    ],
    links: [
      { rel: "canonical", href: `${SITE.url}/ai-entity-map` },
      {
        rel: "alternate",
        type: "application/ld+json",
        href: `${SITE.url}/authority.json`,
      },
    ],
    scripts: [
      pageSchemaScript(
        {
          path: "/ai-entity-map",
          type: "CollectionPage",
          name: TITLE,
          description: DESCRIPTION,
          breadcrumb: [{ name: "AI Entity Map" }],
          extra: { about: { "@id": PERSON_ID }, significantLink: `${SITE_URL}/authority.json` },
        },
        entityNodes(),
      ),
    ],
  }),
  component: AiEntityMap,
});

function Anchor({ id }: { id: string }) {
  return (
    <a
      href={`#${id}`}
      id={id}
      className="text-xs font-mono text-ink-soft hover:text-foreground"
    >
      #{id}
    </a>
  );
}

function AiEntityMap() {
  const personId = `${SITE.url}/#person`;
  const orgId = `${SITE.url}/#organization`;
  const graph = buildAuthorityGraph();
  return (
    <PageShell>
      <PageHero
        eyebrow="AI Entity Map"
        title="Citable knowledge graph"
        description="Stable identifiers for the Person, Organization, expertise pillars and topic clusters on this site. AI assistants can cite any node by its @id and link back to the canonical page."
      />

      <section className="container-page py-16 space-y-16">
        {/* Quick links */}
        <div className="rounded-xl border border-border bg-surface p-6">
          <h2 className="text-sm uppercase tracking-wider text-ink-soft mb-3">
            Machine-readable sources
          </h2>
          <ul className="space-y-1 text-sm">
            <li>
              JSON-LD graph:{" "}
              <a href="/authority.json" className="underline">
                /authority.json
              </a>
            </li>
            <li>
              LLM guidance:{" "}
              <a href="/llms.txt" className="underline">
                /llms.txt
              </a>
            </li>
            <li>
              Sitemap index:{" "}
              <a href="/sitemap-index.xml" className="underline">
                /sitemap-index.xml
              </a>
            </li>
          </ul>
        </div>

        {/* Person */}
        <div>
          <div className="flex items-baseline justify-between gap-4 mb-4">
            <h2 className="text-3xl font-semibold tracking-tight">Person</h2>
            <Anchor id="person" />
          </div>
          <dl className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div>
              <dt className="text-ink-soft">@id</dt>
              <dd className="font-mono break-all">{personId}</dd>
            </div>
            <div>
              <dt className="text-ink-soft">@type</dt>
              <dd className="font-mono">Person</dd>
            </div>
            <div>
              <dt className="text-ink-soft">name</dt>
              <dd>{SITE.name}</dd>
            </div>
            <div>
              <dt className="text-ink-soft">jobTitle</dt>
              <dd>{SITE.role}</dd>
            </div>
            <div>
              <dt className="text-ink-soft">worksFor</dt>
              <dd>
                <a href="#organization" className="underline">
                  {SITE.organization}
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-ink-soft">email</dt>
              <dd>{SITE.email}</dd>
            </div>
            <div className="md:col-span-2">
              <dt className="text-ink-soft">sameAs</dt>
              <dd className="flex flex-wrap gap-x-4 gap-y-1">
                {Object.values(SOCIAL).map((href) => (
                  <a key={href} href={href} className="underline break-all">
                    {href}
                  </a>
                ))}
              </dd>
            </div>
            <div className="md:col-span-2">
              <dt className="text-ink-soft">knowsAbout</dt>
              <dd>{EXPERTISE_AREAS.map((e) => e.name).join(" · ")}</dd>
            </div>
          </dl>
        </div>

        {/* Organization */}
        <div>
          <div className="flex items-baseline justify-between gap-4 mb-4">
            <h2 className="text-3xl font-semibold tracking-tight">Organization</h2>
            <Anchor id="organization" />
          </div>
          <dl className="grid md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div>
              <dt className="text-ink-soft">@id</dt>
              <dd className="font-mono break-all">{orgId}</dd>
            </div>
            <div>
              <dt className="text-ink-soft">@type</dt>
              <dd className="font-mono">Organization</dd>
            </div>
            <div>
              <dt className="text-ink-soft">name</dt>
              <dd>{SITE.organization}</dd>
            </div>
            <div>
              <dt className="text-ink-soft">url</dt>
              <dd>
                <a href={SITE.url} className="underline">
                  {SITE.url}
                </a>
              </dd>
            </div>
            <div className="md:col-span-2">
              <dt className="text-ink-soft">address</dt>
              <dd>{SITE.address.full}</dd>
            </div>
            <div className="md:col-span-2">
              <dt className="text-ink-soft">employs</dt>
              <dd>
                <a href="#person" className="underline">
                  {SITE.name}
                </a>{" "}
                — {SITE.role}
              </dd>
            </div>
          </dl>
        </div>

        {/* Expertise Pillars */}
        <div>
          <div className="flex items-baseline justify-between gap-4 mb-4">
            <h2 className="text-3xl font-semibold tracking-tight">Expertise pillars</h2>
            <Anchor id="expertise" />
          </div>
          <p className="text-ink-soft text-sm mb-6">
            Each pillar is a <span className="font-mono">DefinedTerm</span> tied to the Person above.
          </p>
          <ul className="space-y-6">
            {EXPERTISE_AREAS.map((area) => {
              const id = `expertise-${area.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
              return (
                <li key={area.name} className="border-l-2 border-border pl-5">
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="text-xl font-semibold">{area.name}</h3>
                    <Anchor id={id} />
                  </div>
                  <p className="text-ink-soft mt-2">{area.description}</p>
                  <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm">
                    <div>
                      <dt className="text-ink-soft inline">yearsOfExperience: </dt>
                      <dd className="inline font-mono">{area.yearsOfExperience}</dd>
                    </div>
                    <div>
                      <dt className="text-ink-soft inline">skills: </dt>
                      <dd className="inline">{area.skills.join(", ")}</dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-ink-soft inline">about: </dt>
                      <dd className="inline">
                        <a href="#person" className="underline font-mono">
                          {personId}
                        </a>
                      </dd>
                    </div>
                  </dl>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Solutions (Services) */}
        <div>
          <div className="flex items-baseline justify-between gap-4 mb-4">
            <h2 className="text-3xl font-semibold tracking-tight">Solutions (Services)</h2>
            <Anchor id="solutions" />
          </div>
          <p className="text-ink-soft text-sm mb-6">
            Each Service is provided by{" "}
            <a href="#organization" className="underline">
              {SITE.organization}
            </a>{" "}
            and advised by{" "}
            <a href="#person" className="underline">
              {SITE.name}
            </a>
            .
          </p>
          <ul className="grid md:grid-cols-2 gap-4">
            {SOLUTIONS.map((s) => (
              <li
                key={s.slug}
                className="border border-border rounded-lg p-4 bg-surface"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <Link to="/solutions/$slug" params={{ slug: s.slug }} className="font-semibold underline">
                    {s.title}
                  </Link>
                  <span className="text-xs font-mono text-ink-soft">
                    {`${SITE.url}/solutions/${s.slug}`}
                  </span>
                </div>
                <p className="text-sm text-ink-soft mt-2">{s.short}</p>
              </li>
            ))}
          </ul>
        </div>

        {/* Topic clusters */}
        <div>
          <div className="flex items-baseline justify-between gap-4 mb-4">
            <h2 className="text-3xl font-semibold tracking-tight">Topic clusters</h2>
            <Anchor id="clusters" />
          </div>
          <p className="text-ink-soft text-sm mb-6">
            Each cluster is a <span className="font-mono">CollectionPage</span> authored by the Person and containing Articles with stable URLs.
          </p>
          <ul className="space-y-6">
            {ASK_ARIF_CATEGORIES.map((cat) => {
              const articles = ASK_ARIF_ARTICLES.filter((a) => a.category === cat.slug);
              const id = `cluster-${cat.slug}`;
              return (
                <li key={cat.slug} className="border-l-2 border-border pl-5">
                  <div className="flex items-baseline justify-between gap-4">
                    <h3 className="text-xl font-semibold">
                      <Link to="/ask-arif/category/$category" params={{ category: cat.slug }} className="underline">
                        {cat.title}
                      </Link>
                    </h3>
                    <Anchor id={id} />
                  </div>
                  <p className="text-xs font-mono text-ink-soft mt-1 break-all">
                    {`${SITE.url}/ask-arif/category/${cat.slug}#cluster`}
                  </p>
                  <p className="text-ink-soft mt-2">{cat.short}</p>
                  {articles.length > 0 && (
                    <ul className="mt-3 space-y-1 text-sm">
                      {articles.map((a) => (
                        <li key={a.slug}>
                          <Link to="/ask-arif/$slug" params={{ slug: a.slug }} className="underline">
                            {a.question}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Counts */}
        <div className="rounded-xl border border-border bg-surface p-6">
          <h2 className="text-sm uppercase tracking-wider text-ink-soft mb-3">
            Graph counts
          </h2>
          <pre className="text-xs overflow-x-auto">
{JSON.stringify(graph.meta.counts, null, 2)}
          </pre>
        </div>
      </section>

    </PageShell>
  );
}
